import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, onValue, remove, set } from 'firebase/database';
import { Paper, Container, List, ListItem, ListItemIcon, ListItemText, Checkbox, Box, IconButton, Typography, Snackbar, Alert } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import Navbar from './Navbar';
import CustomDayView from './CustomView';

import fc from '../firebaseConfig';

const db = getDatabase(fc);

const getEvents = (callback) => {
  const eventsRef = ref(db, 'events');
  onValue(eventsRef, (snapshot) => {
    const data = snapshot.val();
    const events = data ? Object.entries(data).map(([id, event]) => ({ ...event, id })) : [];
    callback(events);
  });
};

const getEmployees = (callback) => {
  const employeesRef = ref(db, 'employees');
  onValue(employeesRef, (snapshot) => {
    const data = snapshot.val();
    const employees = data ? Object.entries(data).map(([id, employee]) => ({ ...employee, id })) : [];
    callback(employees);
  });
};

const removeOldEvents = () => {
  const eventsRef = ref(db, 'events');
  const fourteenDaysAgo = new Date();
  fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14000);

  onValue(eventsRef, (snapshot) => {
    const data = snapshot.val();
    if (data) {
      Object.entries(data).forEach(([id, event]) => {
        if (new Date(event.endDate) < fourteenDaysAgo) {
          remove(ref(db, `events/${id}`)).catch((error) => {
            console.error("Error removing old event: ", error);
          });
        }
      });
    }
  });
};

const deleteEvent = (eventId, callback) => {
  const eventRef = ref(db, `events/${eventId}`);
  remove(eventRef).then(() => {
    getEvents(callback);
  }).catch((error) => {
    console.error("Error removing event: ", error);
  });
};

const removeUndefinedEvent = () => {
  const eventRef = ref(db, 'events/undefined');
  remove(eventRef).catch((error) => {
    console.error("Error removing event with id 'undefined': ", error);
  });
};

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isEmployeeDrawerOpen, setIsEmployeeDrawerOpen] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('LOGGED_IN');
    if (!isLoggedIn) {
      navigate('/');
      return;
    }

    removeUndefinedEvent();
    removeOldEvents();
    getEvents(setEvents);
    getEmployees((data) => {
      setEmployees(data);
      setSelectedEmployees(data.map(emp => emp.id));
    });

    const intervalId = setInterval(() => {
      if (localStorage.getItem('SAVE') === 'true') {
        setSnackbarMessage('Poprawnie zaktualizowano usługę');
        //wyświetl SnackBar z napisem "Pomyślnie zmodyfikowano usługę"
        setOpenSnackbar(true);
        localStorage.removeItem('SAVE');
      }
      if (localStorage.getItem('DELETE') === 'true') {
        setSnackbarMessage('Poprawnie usunięto usługę');
        //wyświetl SnackBar z napisem "Pomyślnie usunięto usługę"
        setOpenSnackbar(true);
        localStorage.removeItem('DELETE')
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [navigate]);

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleDelete = (eventId) => {
    deleteEvent(eventId, setEvents);
  };

  const handleSave = (updatedEvent) => {
    const eventRef = ref(db, `events/${updatedEvent.id}`);
    const { id, title, startDate, endDate, color, employeeName, employeeNumber, name, phone } = updatedEvent;
    const cleanEvent = { title, startDate, endDate, color, employeeName, employeeNumber, name, phone };

    set(eventRef, cleanEvent).then(() => {
      getEvents(setEvents);
    }).catch((error) => {
      console.error("Error saving event: ", error);
    });
  };

  const handleEmployeeChange = (employeeId, isChecked) => {
    setSelectedEmployees((prev) =>
      isChecked ? [...prev, employeeId] : prev.filter(id => id !== employeeId)
    );
  };

  const handlePreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

const handleDateChange = (e) => {
  const selectedDate = e.target.value;
  
  if (selectedDate) {
    const newDate = new Date(selectedDate);
    if (!isNaN(newDate.getTime())) {
      setCurrentDate(newDate);
    } 
  }
};


  const filteredEvents = events.filter(event => {
    const employee = employees.find(emp => emp.name === event.employeeName);
    return selectedEmployees.includes(employee?.id) &&
      new Date(event.startDate).toDateString() === currentDate.toDateString();
  });

  const formattedEvents = filteredEvents.map((event) => ({
    ...event,
    startDate: new Date(event.startDate),
    endDate: new Date(event.endDate),
  }));

  return (
    <Container maxWidth={false} disableGutters sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <Paper elevation={3} sx={{ p: 2, display: 'flex', alignItems: 'center', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', alignItems: 'center', flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: '25rem' }}>
              <IconButton onClick={handlePreviousDay} style={{ fontSize: '3rem' }}>
                <ChevronLeft style={{ fontSize: 'inherit' }} />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ textTransform: 'uppercase', marginBottom: '8px' }}>
                {currentDate.toLocaleDateString('pl-PL', { weekday: 'long' })}
              </Typography>
              <input
                type="date"
                value={currentDate.toISOString().split('T')[0]}
                onChange={handleDateChange}
                style={{ textAlign: 'center', border: 'none', fontSize: '1.5rem', maxWidth: '16rem' }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '25rem' }}>
              <IconButton onClick={handleNextDay} style={{ fontSize: '3rem' }}>
                <ChevronRight style={{ fontSize: 'inherit' }} />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginLeft: '-150px' }}>
              <Typography variant="h6">
                <a
                  href="/CalendarWeek"
                  style={{
                    color: 'black',
                    fontSize: '25px',
                    textDecoration: 'underline',
                    transition: 'color 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.color = 'blue'}
                  onMouseLeave={(e) => e.target.style.color = 'black'}
                >
                  Widok Tygodniowy
                </a>

              </Typography>
            </Box>
          </Box>
        </Paper>



        <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 150,
              left: 0,
              height: '100%',
              zIndex: 10,
              display: 'flex',
            }}
          >
            <Box
              onMouseEnter={() => setIsEmployeeDrawerOpen(true)}
              sx={{
                width: 20,
                backgroundColor: 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                height: '100%',
                zIndex: 11,
                position: 'fixed',
                left: 0,
                top: 65
              }}
            >
              <ChevronRight />
            </Box>

            <Box
              onMouseLeave={() => setIsEmployeeDrawerOpen(false)}
              sx={{
                width: isEmployeeDrawerOpen ? '250px' : '0px',
                transition: 'width 0.3s ease',
                overflowY: 'auto', // Add this for vertical scrolling
                overflowX: 'hidden', // Hide horizontal scrollbars if needed
                backgroundColor: '#ffffff',
                borderRight: isEmployeeDrawerOpen ? '4px solid #00' : 'none',
                height: '100%',
                position: 'fixed',
                left: 0,
                zIndex: 10,
                top: 65,
                maxHeight: 'calc(100% - 65px)', // Ensure the drawer doesn't exceed viewport height
              }}
            >
              <Typography variant="h6" sx={{ p: 2, display: isEmployeeDrawerOpen ? 'block' : 'none' }}>Pracownicy</Typography>
              <List sx={{ display: isEmployeeDrawerOpen ? 'block' : 'none' }}>
                {employees.map((employee) => (
                  <ListItem key={employee.id}>
                    <ListItemIcon>
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: employee.color,
                          borderRadius: '10%',
                          border: '2px solid black'
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText primary={employee.name} />
                    <Checkbox
                      edge="end"
                      checked={selectedEmployees.includes(employee.id)}
                      onChange={(e) => handleEmployeeChange(employee.id, e.target.checked)}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

          </Box>

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              marginLeft: '40px',
            }}
          >
            <Box sx={{ flexGrow: 1, overflow: 'hidden', height: '100%' }}>
              <CustomDayView
                events={formattedEvents}
                employees={employees.filter(emp => selectedEmployees.includes(emp.id))}
                onSave={handleSave}
                onDelete={handleDelete}
                currentDate={currentDate}
                sx={{ width: '100%', height: '100%' }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Snackbar open={openSnackbar} autoHideDuration={2000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Calendar;